import React, { useState, useRef, useEffect, useMemo } from 'react'

import styled from 'styled-components'

import { FiPlus } from 'react-icons/fi'

import P from '../../../../components/typography/P'

import RichTextbox from '../../../../components/RichTextbox'
import Phrases from './components/Phrases'

import { debounce } from '../../../../utils/util'

const PhrasedRichTextBox = ({
  onChange,
  value,
  title,
  errorMessage,
  required,
  subHeading,
  phrases,
}) => {
  const [showPhrases, setShowPhrases] = useState(false)
  const containerRef = useRef(null)
  const [updatedPhrases, setUpdatedPhrases] = useState(phrases)

  const removeUsedPhrases = (str) => {
    // 1. Clean the string: remove HTML tags and all whitespace
    const cleanedString = str.replace(/<[^>]+>/g, '').replace(/\s+/g, '')

    // 2. Filter out any phrase that exists inside the cleaned string
    return phrases.filter((phrase) => {
      const cleanedPhrase = phrase.replace(/\s+/g, '') // remove spaces in phrase too
      return !cleanedString.includes(cleanedPhrase)
    })
  }

  const handleAddPhrase = (phrase) => {
    const trimmedValue = value?.trim()
    const hasContent =
      trimmedValue && trimmedValue.replace(/<[^>]*>/g, '').trim()
    const newValue = hasContent
      ? trimmedValue + `<p>${phrase}</p>`
      : `<p>${phrase}</p>`
    onChange(newValue)

    setShowPhrases(false)
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target) &&
        showPhrases
      ) {
        setShowPhrases(false)
      }
    }

    if (showPhrases) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showPhrases])

  const debouncedUpdate = useMemo(
    () => debounce((val) => setUpdatedPhrases(removeUsedPhrases(val)), 500),
    [] // run only once
  )

  useEffect(() => {
    debouncedUpdate(value)
  }, [value, debouncedUpdate])

  return (
    <StyledPhrasedRichTextBox ref={containerRef}>
      <Header>
        <Heading>
          <P color={errorMessage ? 'red' : '#0E1324'}>
            {title} {required && <span className="required">*</span>}
          </P>
          <P>{subHeading}</P>
        </Heading>
        <ShowPhrasesButton>
          <P size="0.7rem">Pre-written phrases</P>
          <FiPlus
            className="icon"
            onClick={() => setShowPhrases(!showPhrases)}
          />
        </ShowPhrasesButton>
      </Header>

      <RichTextbox
        content={value}
        setContent={onChange}
        required={required}
        marginBottom="1rem"
        className={errorMessage && 'has-error'}
        errorMessage={errorMessage}
      />
      {showPhrases && (
        <Phrases
          onAddPhrase={handleAddPhrase}
          phrases={updatedPhrases}
          setShowPhrases={setShowPhrases}
        />
      )}
    </StyledPhrasedRichTextBox>
  )
}

const Header = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;

  .icon {
    width: 0.875rem;
    height: 0.875rem;
    cursor: pointer;
    font-size: 0.7rem;
    color: rgb(253, 32, 85);
    border-radius: 50%;
    background: rgb(255, 226, 233);
  }

  .required {
    color: red;
  }
`

const Heading = styled.div`
  display: flex;
  flex-direction: column;
`

const ShowPhrasesButton = styled.div`
  display: flex;
  gap: 0.25rem;
  align-items: center;
`

const StyledPhrasedRichTextBox = styled.div`
  position: relative;
  width: 100%;
`

export default PhrasedRichTextBox
